import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, Print as PrintIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getCompanyInfo } from '../../../services/migration/companyDataService';
import { exportHepsiburadaLabelToPdf } from '../utils/hepsiburadaLabelPdf';
import {
  HB_LABEL_BORDER,
  HB_ORANGE,
  buildFullAddressLine,
  deepFindString,
  formatLabelDateTime,
  generateBarcodeDataUrl,
  normalizeHepsiburadaAddress,
  parseOrderAddress,
  parseOrderCargoInfo,
  resolveCargoLogoSrc,
  resolveDeliveryTypeLabel,
  resolveHepsiburadaBarcodeValue,
  resolveHepsiburadaDesi,
  resolveHepsiburadaPackageNumber,
  stripHbOrderPrefix,
  toTurkishUpper,
} from '../utils/hepsiburadaPrintUtils';
import {
  A5_PORTRAIT_PREVIEW_MAX_WIDTH,
  A5_PORTRAIT_PRINT_WIDTH,
  a5PortraitPrintSx,
} from '../utils/printPageStyles';

interface HepsiburadaPrintModalProps {
  open: boolean;
  onClose: () => void;
  order: any;
  companyId?: string;
}

const LABEL_FONT = 'Arial, Helvetica, sans-serif';

const labelCellSx = {
  border: `1px solid ${HB_LABEL_BORDER}`,
  px: 0.75,
  py: 0.45,
  fontFamily: LABEL_FONT,
};

const labelTitleSx = {
  fontWeight: 700,
  fontSize: '7pt',
  letterSpacing: '0.02em',
  color: '#444',
  mb: 0.2,
  fontFamily: LABEL_FONT,
  lineHeight: 1.15,
};

const labelValueSx = (bold = false) => ({
  fontWeight: bold ? 700 : 500,
  fontSize: '8pt',
  color: '#111',
  wordBreak: 'break-word' as const,
  fontFamily: LABEL_FONT,
  lineHeight: 1.25,
});

const HepsiburadaPrintModal: React.FC<HepsiburadaPrintModalProps> = ({
  open,
  onClose,
  order,
  companyId,
}) => {
  const { t } = useTranslation('salesSystem');
  const labelRef = useRef<HTMLDivElement>(null);
  const [companyName, setCompanyName] = useState('');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      if (!companyId || !open) return;
      try {
        const info = await getCompanyInfo(companyId);
        if (info?.companyName) {
          setCompanyName(info.companyName);
        }
      } catch (error) {
        console.error('[HEPSIBURADA PRINT MODAL] Şirket bilgileri alınamadı:', error);
      }
    };
    fetchCompanyInfo();
  }, [companyId, open]);

  const parsedShippingAddress = useMemo(() => parseOrderAddress(order?.shippingAddress), [order]);
  const parsedBillingAddress = useMemo(() => parseOrderAddress(order?.billingAddress), [order]);
  const parsedCargoInfo = useMemo(() => parseOrderCargoInfo(order?.cargoInfo), [order]);

  const shippingAddress = useMemo(
    () => normalizeHepsiburadaAddress(parsedShippingAddress),
    [parsedShippingAddress]
  );
  const billingAddress = useMemo(
    () => normalizeHepsiburadaAddress(parsedBillingAddress),
    [parsedBillingAddress]
  );

  const cargoBarcodeValue = useMemo(
    () => resolveHepsiburadaBarcodeValue(order, parsedCargoInfo),
    [order, parsedCargoInfo]
  );
  const packageNumber = useMemo(
    () => resolveHepsiburadaPackageNumber(order, parsedCargoInfo),
    [order, parsedCargoInfo]
  );
  const desi = useMemo(() => resolveHepsiburadaDesi(order, parsedCargoInfo), [order, parsedCargoInfo]);

  const barcodeDataUrl = useMemo(
    () =>
      cargoBarcodeValue
        ? generateBarcodeDataUrl(cargoBarcodeValue, { height: 96, barWidth: 2.4 })
        : '',
    [cargoBarcodeValue]
  );

  const handlePrint = async () => {
    if (!labelRef.current || isGeneratingPdf) return;

    setPdfError(null);
    setIsGeneratingPdf(true);

    try {
      const orderLabel = stripHbOrderPrefix(order?.orderNumber || order?.id || 'etiket');
      await exportHepsiburadaLabelToPdf(labelRef.current, {
        fileName: `Hepsiburada_Kargo_Etiketi_${orderLabel}.pdf`,
        tryAutoPrint: true,
      });
    } catch (error) {
      console.error('[HEPSIBURADA PRINT MODAL] PDF oluşturma hatası:', error);
      setPdfError(
        error instanceof Error && error.message.includes('Pop-up')
          ? error.message
          : t('ecommerceOrders.hepsiburadaPrintModal.pdfError')
      );
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const orderItems = useMemo(() => {
    if (!order) return [];
    let items = order.items;
    if (typeof items === 'string') {
      try {
        items = JSON.parse(items);
      } catch {
        items = [];
      }
    }
    if (!Array.isArray(items)) {
      items = items && typeof items === 'object' ? [items] : [];
    }
    return items;
  }, [order]);

  if (!order) return null;

  const cargoCompany =
    parsedCargoInfo?.cargoCompany ||
    parsedCargoInfo?.cargoProviderName ||
    order.shippingCompany ||
    deepFindString(parsedCargoInfo, ['cargoCompany', 'cargoProviderName']) ||
    'hepsiJET';
  const cargoLogoSrc = resolveCargoLogoSrc(cargoCompany);
  const deliveryTypeLabel = resolveDeliveryTypeLabel(parsedCargoInfo);
  const { date: labelDate, time: labelTime } = formatLabelDateTime(order.date);
  const orderNumber = stripHbOrderPrefix(order.orderNumber || order.n11OrderId);
  const recipientName =
    shippingAddress?.name ||
    order.customerName ||
    t('ecommerceOrders.table.customerNameUnknown');
  const senderName = companyName || t('ecommerceOrders.hepsiburadaPrintModal.defaultSender');
  const totalQuantity = orderItems.reduce(
    (sum: number, item: any) => sum + Number(item?.quantity ?? 1),
    0
  );
  const packageIndex = '1/1';
  const workArea = deepFindString(parsedCargoInfo, ['workArea', 'calismaAlani', 'operationArea']) || '-';
  const departureCity =
    deepFindString(parsedCargoInfo, ['departureCity', 'originCity', 'cikis', 'dispatchCity']) || '-';
  const compactProducts = orderItems.length > 2;
  const productRowFont = compactProducts ? '5.5pt' : '6pt';
  const productHeaderFont = compactProducts ? '5.5pt' : '6pt';

  const renderFoldLine = () => (
    <Box
      className="hb-fold-line"
      sx={{
        my: 1.25,
        height: 16,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box sx={{ flex: 1, borderTop: `2px dashed ${HB_LABEL_BORDER}` }} />
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          border: `2px dashed ${HB_LABEL_BORDER}`,
          bgcolor: '#fff',
          mx: 0.5,
          flexShrink: 0,
        }}
      />
      <Box sx={{ flex: 1, borderTop: `2px dashed ${HB_LABEL_BORDER}` }} />
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '92vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: HB_ORANGE,
          color: 'white',
        }}
      >
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <PrintIcon sx={{ mr: 1 }} />
          {t('ecommerceOrders.hepsiburadaPrintModal.title')}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: '#f5f5f5' }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
          <Box
            ref={labelRef}
            className="hb-label-sheet"
            sx={{
              width: '100%',
              maxWidth: A5_PORTRAIT_PREVIEW_MAX_WIDTH,
              bgcolor: 'white',
              boxSizing: 'border-box',
              fontFamily: LABEL_FONT,
              boxShadow: isGeneratingPdf ? 'none' : '0 1px 4px rgba(0,0,0,0.08)',
              ...a5PortraitPrintSx,
              '@media print': {
                ...a5PortraitPrintSx['@media print'],
                width: A5_PORTRAIT_PRINT_WIDTH,
                maxWidth: A5_PORTRAIT_PRINT_WIDTH,
              },
            }}
          >
              {/* Üst bölüm — dış yüz (teslimat bilgileri) */}
              <Box
                className="hb-label-section"
                sx={{
                  border: `1.5px solid ${HB_LABEL_BORDER}`,
                  p: 1.25,
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr auto auto',
                    gap: 0.5,
                    mb: 0.5,
                  }}
                >
                  <Box sx={labelCellSx}>
                    <Typography sx={labelTitleSx}>
                      {t('ecommerceOrders.hepsiburadaPrintModal.destinationCity')}
                    </Typography>
                    <Typography sx={labelValueSx(true)}>
                      {toTurkishUpper(shippingAddress?.city || '-')}
                    </Typography>
                  </Box>
                  <Box sx={labelCellSx}>
                    <Typography sx={labelTitleSx}>
                      {t('ecommerceOrders.hepsiburadaPrintModal.destinationDistrict')}
                    </Typography>
                    <Typography sx={labelValueSx(true)}>
                      {toTurkishUpper(shippingAddress?.district || '-')}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      ...labelCellSx,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 36,
                      fontWeight: 700,
                      fontSize: '8pt',
                    }}
                  >
                    {toTurkishUpper(shippingAddress?.neighborhood?.slice(0, 3) || '—')}
                  </Box>
                  <Box
                    sx={{
                      ...labelCellSx,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 36,
                      fontWeight: 700,
                      fontSize: '8pt',
                    }}
                  >
                    {packageIndex}
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 0.5,
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography sx={{ ...labelValueSx(), fontFamily: 'monospace' }}>{labelDate}</Typography>
                    <Typography sx={{ ...labelValueSx(), fontFamily: 'monospace' }}>{labelTime}</Typography>
                  </Box>
                  {cargoLogoSrc ? (
                    <Box
                      component="img"
                      src={cargoLogoSrc}
                      alt={cargoCompany}
                      className="hb-cargo-logo"
                      sx={{ maxHeight: 26, maxWidth: 100, objectFit: 'contain' }}
                    />
                  ) : (
                    <Typography sx={{ ...labelValueSx(true), color: HB_ORANGE }}>
                      {toTurkishUpper(cargoCompany)}
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(200px, 1.55fr)',
                    gap: 0.5,
                    mb: 0.5,
                    alignItems: 'stretch',
                  }}
                >
                  <Box sx={labelCellSx}>
                    <Typography sx={labelTitleSx}>
                      {t('ecommerceOrders.hepsiburadaPrintModal.destinationNeighborhood')}
                    </Typography>
                    <Typography sx={labelValueSx(true)}>
                      {toTurkishUpper(shippingAddress?.neighborhood || shippingAddress?.district || '-')}
                    </Typography>
                    <Typography sx={{ ...labelValueSx(), mt: 0.25 }}>
                      {toTurkishUpper(shippingAddress?.district || '')}
                    </Typography>
                  </Box>
                  <Box
                    className="hb-barcode-cell"
                    sx={{
                      ...labelCellSx,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 0.75,
                    }}
                  >
                    {barcodeDataUrl ? (
                      <>
                        <Box
                          component="img"
                          src={barcodeDataUrl}
                          alt={t('ecommerceOrders.hepsiburadaPrintModal.cargoBarcodeAlt')}
                          className="hb-barcode-img"
                          sx={{
                            width: '100%',
                            height: 'auto',
                            minHeight: 72,
                            maxHeight: 88,
                            objectFit: 'contain',
                            display: 'block',
                          }}
                        />
                        <Typography
                          className="hb-barcode-text"
                          sx={{
                            fontSize: '9pt',
                            fontWeight: 600,
                            fontFamily: 'monospace',
                            mt: 0.35,
                            letterSpacing: '0.5px',
                            textAlign: 'center',
                          }}
                        >
                          {cargoBarcodeValue}
                        </Typography>
                      </>
                    ) : (
                      <Typography sx={labelValueSx()}>
                        {t('ecommerceOrders.hepsiburadaPrintModal.barcodeUnavailable')}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: 0.5,
                    mb: 0.5,
                  }}
                >
                  <Box sx={labelCellSx}>
                    <Typography sx={labelTitleSx}>
                      {t('ecommerceOrders.hepsiburadaPrintModal.desi')}
                    </Typography>
                    <Typography sx={labelValueSx()}>{desi}</Typography>
                  </Box>
                  <Box sx={labelCellSx}>
                    <Typography sx={labelTitleSx}>
                      {t('ecommerceOrders.hepsiburadaPrintModal.workArea')}
                    </Typography>
                    <Typography sx={labelValueSx()}>{workArea}</Typography>
                  </Box>
                  <Box sx={labelCellSx}>
                    <Typography sx={labelTitleSx}>
                      {t('ecommerceOrders.hepsiburadaPrintModal.departure')}
                    </Typography>
                    <Typography sx={labelValueSx()}>{toTurkishUpper(departureCity)}</Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, mb: 0.5 }}>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Typography sx={{ ...labelTitleSx, minWidth: 72 }}>
                      {t('ecommerceOrders.hepsiburadaPrintModal.sender')}:
                    </Typography>
                    <Typography sx={labelValueSx()}>{senderName}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Typography sx={{ ...labelTitleSx, minWidth: 72 }}>
                      {t('ecommerceOrders.hepsiburadaPrintModal.recipient')}:
                    </Typography>
                    <Typography sx={labelValueSx(true)}>{recipientName}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
                    <Typography sx={{ ...labelTitleSx, minWidth: 72 }}>
                      {t('ecommerceOrders.hepsiburadaPrintModal.address')}:
                    </Typography>
                    <Typography sx={labelValueSx()}>{buildFullAddressLine(shippingAddress)}</Typography>
                  </Box>
                </Box>

                {deliveryTypeLabel && (
                  <Box
                    sx={{
                      border: `1px solid ${HB_LABEL_BORDER}`,
                      bgcolor: '#f9f9f9',
                      textAlign: 'center',
                      py: 0.4,
                      mb: 0.5,
                      fontWeight: 700,
                      fontSize: '7.5pt',
                      fontFamily: LABEL_FONT,
                    }}
                  >
                    {deliveryTypeLabel}
                  </Box>
                )}

                <Box className="hb-product-section" sx={{ borderTop: `1px solid ${HB_LABEL_BORDER}`, pt: 0.5 }}>
                  <Box
                    className="hb-product-header"
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto auto',
                      gap: 0.5,
                      mb: 0.25,
                      fontWeight: 700,
                      fontSize: productHeaderFont,
                      color: '#555',
                    }}
                  >
                    <Typography sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
                      {t('ecommerceOrders.hepsiburadaPrintModal.productName')}
                    </Typography>
                    <Typography sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
                      {t('ecommerceOrders.hepsiburadaPrintModal.productCode')}
                    </Typography>
                    <Typography sx={{ fontSize: 'inherit', fontWeight: 'inherit', minWidth: 28, textAlign: 'center' }}>
                      {t('ecommerceOrders.hepsiburadaPrintModal.quantity')}
                    </Typography>
                  </Box>
                  {orderItems.length > 0 ? (
                    orderItems.map((item: any, index: number) => {
                      const productName =
                        item?.name || item?.productName || t('ecommerceOrders.marketplaces.unknown');
                      const stockCode =
                        item?.merchantSku ||
                        item?.stockCode ||
                        item?.sku ||
                        item?.n11StockCode ||
                        item?.hbSku ||
                        '-';
                      const quantity = Number(item?.quantity ?? 1);
                      return (
                        <Box
                          key={`${item?.id || stockCode || index}-${index}`}
                          className="hb-product-row"
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr auto auto',
                            gap: 0.5,
                            py: compactProducts ? 0.1 : 0.15,
                            borderTop: index > 0 ? '1px dashed #ccc' : 'none',
                            fontSize: productRowFont,
                            lineHeight: 1.15,
                            pageBreakInside: 'avoid',
                          }}
                        >
                          <Typography sx={{ fontSize: 'inherit', lineHeight: 'inherit' }}>{productName}</Typography>
                          <Typography
                            sx={{
                              fontSize: 'inherit',
                              fontFamily: 'monospace',
                              minWidth: 72,
                              lineHeight: 'inherit',
                            }}
                          >
                            {stockCode}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 'inherit',
                              minWidth: 28,
                              textAlign: 'center',
                              lineHeight: 'inherit',
                            }}
                          >
                            {quantity}
                          </Typography>
                        </Box>
                      );
                    })
                  ) : (
                    <Typography sx={{ fontSize: '6pt', color: 'text.secondary' }}>
                      {t('ecommerceOrders.hepsiburadaPrintModal.noProducts')}
                    </Typography>
                  )}
                </Box>
              </Box>

              {renderFoldLine()}

              {/* Alt bölüm — katlanan özel bilgiler */}
              <Box
                className="hb-label-section"
                sx={{
                  border: `1.5px solid ${HB_LABEL_BORDER}`,
                  p: 1.25,
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: 0.5,
                    mb: 0.5,
                  }}
                >
                  <Box sx={labelCellSx}>
                    <Typography sx={labelTitleSx}>
                      {t('ecommerceOrders.hepsiburadaPrintModal.seller')}
                    </Typography>
                    <Typography sx={labelValueSx()}>{senderName}</Typography>
                  </Box>
                  <Box sx={labelCellSx}>
                    <Typography sx={labelTitleSx}>
                      {t('ecommerceOrders.hepsiburadaPrintModal.orderNoShort')}
                    </Typography>
                    <Typography sx={{ ...labelValueSx(true), fontSize: '7.5pt' }}>
                      {orderNumber || '-'}
                    </Typography>
                  </Box>
                  <Box sx={labelCellSx}>
                    <Typography sx={labelTitleSx}>
                      {t('ecommerceOrders.hepsiburadaPrintModal.deliveryNoShort')}
                    </Typography>
                    <Typography sx={{ ...labelValueSx(true), fontFamily: 'monospace', fontSize: '7.5pt' }}>
                      {cargoBarcodeValue || packageNumber || '-'}
                    </Typography>
                  </Box>
                  <Box sx={labelCellSx}>
                    <Typography sx={labelTitleSx}>
                      {t('ecommerceOrders.hepsiburadaPrintModal.desi')}
                    </Typography>
                    <Typography sx={labelValueSx()}>{desi}</Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: 0.5,
                    mb: 0.5,
                  }}
                >
                  <Box sx={labelCellSx}>
                    <Typography sx={labelTitleSx}>
                      {t('ecommerceOrders.hepsiburadaPrintModal.invoiceAddress')}
                    </Typography>
                    <Typography sx={{ ...labelValueSx(), fontSize: '7pt' }}>
                      {billingAddress?.name && `${billingAddress.name} — `}
                      {buildFullAddressLine(billingAddress || shippingAddress)}
                    </Typography>
                    {billingAddress?.taxNumber && (
                      <Typography sx={{ ...labelValueSx(), fontSize: '7pt', mt: 0.25 }}>
                        {t('ecommerceOrders.hepsiburadaPrintModal.taxNumber')}: {billingAddress.taxNumber}
                      </Typography>
                    )}
                    {billingAddress?.taxOffice && (
                      <Typography sx={{ ...labelValueSx(), fontSize: '7pt' }}>
                        {t('ecommerceOrders.hepsiburadaPrintModal.taxOffice')}: {billingAddress.taxOffice}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      ...labelCellSx,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 56,
                    }}
                  >
                    <Typography sx={labelTitleSx}>
                      {t('ecommerceOrders.hepsiburadaPrintModal.quantity')}
                    </Typography>
                    <Typography sx={{ ...labelValueSx(true), fontSize: '8pt' }}>
                      {totalQuantity || 1}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderTop: `1px solid ${HB_LABEL_BORDER}`,
                    pt: 0.5,
                  }}
                >
                  {cargoLogoSrc ? (
                    <Box
                      component="img"
                      src={cargoLogoSrc}
                      alt={cargoCompany}
                      className="hb-cargo-logo"
                      sx={{ maxHeight: 22, maxWidth: 88, objectFit: 'contain' }}
                    />
                  ) : (
                    <Typography sx={{ ...labelValueSx(true), color: HB_ORANGE }}>
                      {toTurkishUpper(cargoCompany)}
                    </Typography>
                  )}
                  <Typography sx={{ ...labelValueSx(true), fontFamily: 'monospace' }}>{packageIndex}</Typography>
                </Box>
              </Box>
            </Box>
        </Box>
        {pdfError && (
          <Typography color="error" sx={{ px: 2, pb: 1, fontSize: '0.85rem' }}>
            {pdfError}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
        <Button onClick={onClose} variant="outlined" startIcon={<CloseIcon />} disabled={isGeneratingPdf}>
          {t('ecommerceOrders.orderDetailsModal.close')}
        </Button>
        <Button
          onClick={handlePrint}
          variant="contained"
          disabled={isGeneratingPdf}
          startIcon={isGeneratingPdf ? <CircularProgress size={18} color="inherit" /> : <PrintIcon />}
          sx={{
            bgcolor: HB_ORANGE,
            '&:hover': { bgcolor: '#e55500' },
          }}
        >
          {isGeneratingPdf
            ? t('ecommerceOrders.hepsiburadaPrintModal.generatingPdf')
            : t('ecommerceOrders.hepsiburadaPrintModal.print')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HepsiburadaPrintModal;
